import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { AuthShell } from '../components/AuthShell.jsx';

/**
 * /login — returning-user sign in.
 *
 * Deliberately minimal:
 *  - Email + password, that's it
 *  - Generic error on failure (no enumeration)
 *  - No "forgot password" link in Phase 1 (that flow lands later)
 *  - On success, refresh() the auth context then navigate to
 *    the `from` path (if redirected here by ProtectedRoute) or /dashboard
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth();

  // If ProtectedRoute redirected us here, it sets location.state.from
  const from = location.state?.from?.pathname || '/dashboard';

  const [form, setForm] = useState({ email: '', password: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!form.email.trim() || !form.password) {
      setError('Enter your email and password.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/auth/login', {
        email: form.email.trim(),
        password: form.password,
      });

      // Session cookie is now set. Hydrate context.
      await refresh();
      navigate(from, { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
      setSubmitting(false);
    }
  }

  return (
    <AuthShell>
      <div className="auth-card">
        <h1 className="auth-heading">Log in to TocynID.</h1>
        <p className="auth-subheading">
          Welcome back.
        </p>

        {error && <div className="alert alert-error" role="alert">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label className="field-label" htmlFor="l-email">Email</label>
            <input
              id="l-email"
              className="field-input"
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              autoFocus
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="l-password">Password</label>
            <input
              id="l-password"
              className="field-input"
              type="password"
              name="password"
              autoComplete="current-password"
              value={form.password}
              onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? 'Logging in…' : 'Log in'}
          </button>

          <p className="muted text-center" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-5)' }}>
            Don't have an account yet?{' '}
            <Link to="/signup">Start your free trial</Link>
          </p>
        </form>
      </div>
    </AuthShell>
  );
}
